import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { ReactComponent as EyeOpen } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeClosed } from "../../assets/icons/eye-slash.svg";

declare global {
  interface Window {
    google?: any;
    AppleID?: any;
  }
}

const API_BASE = "https://api.logvi-havox.com";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";
const MICROSOFT_TENANT_ID =
  process.env.REACT_APP_MICROSOFT_TENANT_ID || "common";
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  `${window.location.origin}/redefinir-senha`;
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || "";
const GITHUB_REDIRECT_URI =
  process.env.REACT_APP_GITHUB_REDIRECT_URI ||
  `${window.location.origin}/redefinir-senha`;

function useIsMobile(breakpoint: number = 980): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);

    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    // @ts-ignore
    mq.addListener(update);
    // @ts-ignore
    return () => mq.removeListener(update);
  }, [breakpoint]);

  return isMobile;
}

function useExternalScript(src: string, id: string) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;

    if (existing) {
      if (existing.dataset.loaded === "true") {
        setReady(true);
        return;
      }

      const onLoad = () => {
        existing.dataset.loaded = "true";
        setReady(true);
      };

      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      script.dataset.loaded = "true";
      setReady(true);
    };

    script.onerror = () => {
      setReady(false);
    };

    document.body.appendChild(script);

    return () => {};
  }, [src, id]);

  return ready;
}

type RecoveryProvider = "google" | "apple" | "github" | "microsoft";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.195 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4c-7.682 0-14.344 4.337-17.694 10.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.093 0 9.786-1.947 13.346-5.118l-6.19-5.238C29.083 35.091 26.624 36 24 36c-5.174 0-9.623-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.147 5.645l.003-.002 6.19 5.238C36.908 39.18 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M16.365 1.43c0 1.14-.466 2.24-1.18 3.02-.77.84-2.03 1.49-3.08 1.41-.13-1.1.39-2.26 1.14-3.03.76-.79 2.09-1.38 3.12-1.4zM20.93 17.3c-.52 1.19-.77 1.72-1.44 2.76-.93 1.45-2.24 3.25-3.87 3.26-1.45.01-1.82-.95-3.79-.94-1.97.01-2.39.95-3.84.95-1.63.01-2.88-1.6-3.81-3.05-2.61-4.04-2.88-8.77-1.27-11.23 1.15-1.77 2.96-2.8 4.67-2.8 1.75 0 2.85.96 4.29.96 1.39 0 2.23-.96 4.28-.96 1.53 0 3.15.84 4.29 2.29-3.78 2.07-3.17 7.49.49 8.76z"
    />
  </svg>
);

const GithubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M12 .5C5.65.5.5 5.65.5 12.01c0 5.09 3.29 9.41 7.86 10.94.58.1.79-.25.79-.56 0-.27-.01-1.16-.02-2.1-3.2.7-3.88-1.36-3.88-1.36-.52-1.34-1.28-1.69-1.28-1.69-1.04-.72.08-.71.08-.71 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.68 1.25 3.34.95.1-.74.4-1.25.73-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.3 1.18-3.11-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.19 1.19a10.97 10.97 0 0 1 5.81 0c2.22-1.5 3.18-1.19 3.18-1.19.63 1.58.24 2.75.12 3.04.74.81 1.18 1.85 1.18 3.11 0 4.42-2.69 5.39-5.25 5.67.41.35.78 1.04.78 2.11 0 1.52-.01 2.74-.01 3.11 0 .31.2.67.8.56A11.52 11.52 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

const providerMeta: Record<
  RecoveryProvider,
  { label: string; icon: React.ReactNode }
> = {
  google: { label: "Recuperar com Google", icon: <GoogleIcon /> },
  apple: { label: "Recuperar com Apple", icon: <AppleIcon /> },
  github: { label: "Recuperar com GitHub", icon: <GithubIcon /> },
  microsoft: { label: "Recuperar com Microsoft", icon: <MicrosoftIcon /> },
};

const SocialButton: React.FC<{
  provider: RecoveryProvider;
  onClick: () => void;
  disabled?: boolean;
}> = ({ provider, onClick, disabled }) => {
  const meta = providerMeta[provider];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        minHeight: 56,
        height: 56,
        borderRadius: 14,
        border: "1px solid rgba(15, 23, 42, 0.10)",
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .18s ease",
        fontFamily: "Poppins, sans-serif",
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        padding: "0 16px",
        opacity: disabled ? 0.6 : 1,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.borderColor = "rgba(14, 50, 58, 0.18)";
        e.currentTarget.style.background = "#FCFDFD";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.10)";
        e.currentTarget.style.background = "#FFFFFF";
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          minWidth: 22,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </span>

      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {meta.label}
      </span>
    </button>
  );
};

const isMsalCryptoAvailable =
  typeof window !== "undefined" &&
  typeof window.crypto !== "undefined" &&
  typeof window.crypto.subtle !== "undefined";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const isHttps = window.location.protocol === "https:";

const canUseMsal = isMsalCryptoAvailable && (isHttps || isLocalhost);

const msalConfig = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}`,
    redirectUri: `${window.location.origin}/redefinir-senha`,
  },
  cache: {
    cacheLocation: "sessionStorage" as const,
    storeAuthStateInCookie: false,
  },
};

const msalInstance = canUseMsal
  ? new PublicClientApplication(msalConfig)
  : null;

const RecuperarSenhaContainer: React.FC = () => {
  const isMobile = useIsMobile();

  const googleReady = useExternalScript(
    "https://accounts.google.com/gsi/client",
    "google-gsi-script-recovery"
  );

  const appleReady = useExternalScript(
    "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
    "apple-auth-script-recovery"
  );

  const [userId, setUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<RecoveryProvider | null>(
    null
  );
  const [feedback, setFeedback] = useState<{
    type: "error" | "success";
    msg: string;
  } | null>(null);

  const trimmedUserId = useMemo(() => userId.trim(), [userId]);

  useEffect(() => {
    if (!appleReady || !APPLE_CLIENT_ID || !window.AppleID?.auth) return;

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        state: "apple-password-reset",
        usePopup: true,
      });
    } catch {}
  }, [appleReady]);

  const validate = useCallback(() => {
    if (!trimmedUserId || !newPassword || !confirmPassword) {
      setFeedback({
        type: "error",
        msg: "Preencha todos os campos para redefinir a senha.",
      });
      return false;
    }

    if (newPassword.length < 8) {
      setFeedback({
        type: "error",
        msg: "A nova senha deve ter pelo menos 8 caracteres.",
      });
      return false;
    }

    if (newPassword !== confirmPassword) {
      setFeedback({
        type: "error",
        msg: "A confirmação de senha não confere.",
      });
      return false;
    }

    return true;
  }, [trimmedUserId, newPassword, confirmPassword]);

  const handleResetPassword = useCallback(async () => {
    if (loading) return;

    setFeedback(null);

    if (!validate()) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          userId: trimmedUserId,
          senha: newPassword,
          confirmarSenha: confirmPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setFeedback({
          type: "error",
          msg:
            data?.erro ||
            data?.message ||
            "Não foi possível redefinir a senha.",
        });
        return;
      }

      setFeedback({
        type: "success",
        msg: "Senha redefinida com sucesso. Você já pode entrar novamente.",
      });

      setNewPassword("");
      setConfirmPassword("");
    } catch {
      setFeedback({
        type: "error",
        msg: "Não foi possível conectar ao servidor.",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, validate, trimmedUserId, newPassword, confirmPassword]);

  const handleGoogleRecovery = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GOOGLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!googleReady || !window.google?.accounts?.id) {
      setFeedback({
        type: "error",
        msg: "SDK do Google ainda não está pronto.",
      });
      return;
    }

    try {
      setFeedback(null);
      setSocialLoading("google");

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          try {
            if (!response?.credential) {
              throw new Error("Google não retornou credencial válida.");
            }

            const res = await fetch(`${API_BASE}/auth/reset-password/social`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                provider: "google",
                idToken: response.credential,
              }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
              throw new Error(
                data?.erro || "Falha ao iniciar recuperação com Google."
              );
            }

            setFeedback({
              type: "success",
              msg:
                data?.message || "Validação com Google realizada com sucesso.",
            });
          } catch (err: any) {
            setFeedback({
              type: "error",
              msg: err?.message || "Falha ao iniciar recuperação com Google.",
            });
          } finally {
            setSocialLoading(null);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.prompt();
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao iniciar recuperação com Google.",
      });
      setSocialLoading(null);
    }
  }, [googleReady]);

  const handleMicrosoftRecovery = useCallback(async () => {
    if (!MICROSOFT_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_MICROSOFT_CLIENT_ID não configurado.",
      });
      return;
    }

   try {
  setFeedback(null);

  if (!msalInstance) {
    setFeedback({
      type: "error",
      msg:
        "Microsoft indisponível neste ambiente local via IP. Para usar essa opção, acesse por HTTPS ou localhost.",
    });
    return;
  }

  setSocialLoading("microsoft");

  await msalInstance.initialize();

  const response = await msalInstance.loginPopup({
    scopes: ["openid", "profile", "email"],
    prompt: "select_account",
  });

  const idToken = response.idToken;

  if (!idToken) {
    throw new Error("Microsoft não retornou idToken.");
  }

  const res = await fetch(`${API_BASE}/auth/reset-password/social`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      provider: "microsoft",
      idToken,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(
      data?.erro || "Falha ao iniciar recuperação com Microsoft."
    );
  }

  setFeedback({
    type: "success",
    msg: data?.message || "Validação com Microsoft realizada com sucesso.",
  });
} catch (err: any) {
  setFeedback({
    type: "error",
    msg: err?.message || "Falha ao iniciar recuperação com Microsoft.",
  });
} finally {
  setSocialLoading(null);
}
  }, []);

  const handleAppleRecovery = useCallback(async () => {
    if (!APPLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_APPLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!window.AppleID?.auth) {
      setFeedback({
        type: "error",
        msg: "SDK da Apple ainda não carregou.",
      });
      return;
    }

    try {
      setFeedback(null);
      setSocialLoading("apple");

      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;

      if (!idToken) {
        throw new Error("Apple não retornou idToken.");
      }

      const res = await fetch(`${API_BASE}/auth/reset-password/social`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          provider: "apple",
          idToken,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.erro || "Falha ao iniciar recuperação com Apple."
        );
      }

      setFeedback({
        type: "success",
        msg: data?.message || "Validação com Apple realizada com sucesso.",
      });
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao iniciar recuperação com Apple.",
      });
    } finally {
      setSocialLoading(null);
    }
  }, []);

  const handleGithubRecovery = useCallback(() => {
    if (!GITHUB_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GITHUB_CLIENT_ID não configurado.",
      });
      return;
    }

    const state =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    sessionStorage.setItem("github_oauth_state_recovery", state);

    const redirectUri = new URL(GITHUB_REDIRECT_URI);
    redirectUri.searchParams.set("social_provider", "github");
    redirectUri.searchParams.set("flow", "recovery");

    const githubUrl = new URL("https://github.com/login/oauth/authorize");
    githubUrl.searchParams.set("client_id", GITHUB_CLIENT_ID);
    githubUrl.searchParams.set("redirect_uri", redirectUri.toString());
    githubUrl.searchParams.set("scope", "read:user user:email");
    githubUrl.searchParams.set("state", state);

    setSocialLoading("github");
    window.location.href = githubUrl.toString();
  }, []);

  const handleSocialClick = useCallback(
    (provider: RecoveryProvider) => {
      if (loading || socialLoading) return;

      if (provider === "google") return handleGoogleRecovery();
      if (provider === "microsoft") return handleMicrosoftRecovery();
      if (provider === "apple") return handleAppleRecovery();
      if (provider === "github") return handleGithubRecovery();
    },
    [
      loading,
      socialLoading,
      handleGoogleRecovery,
      handleMicrosoftRecovery,
      handleAppleRecovery,
      handleGithubRecovery,
    ]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) {
      handleResetPassword();
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    background:
      "radial-gradient(circle at 8% 10%, rgba(255,156,36,0.16) 0%, rgba(255,156,36,0) 22%), radial-gradient(circle at 90% 12%, rgba(26,103,191,0.28) 0%, rgba(26,103,191,0) 24%), radial-gradient(circle at 84% 74%, rgba(0,192,183,0.17) 0%, rgba(0,192,183,0) 22%), radial-gradient(circle at 14% 78%, rgba(0,177,231,0.10) 0%, rgba(0,177,231,0) 20%), linear-gradient(135deg, #07161B 0%, #0B242B 44%, #0E323A 100%)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: isMobile ? "26px 16px 18px" : "34px 28px 22px",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 540,
    borderRadius: 28,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.18)",
    padding: isMobile ? "24px 18px 22px" : "34px 34px 28px",
    boxSizing: "border-box",
    zIndex: 2,
    marginTop: isMobile ? "26px" : "50px",
    backdropFilter: "blur(10px)",
  };

  const formTitleStyle: React.CSSProperties = {
    margin: "10px 0 8px",
    color: "#0F172A",
    fontSize: isMobile ? 30 : 42,
    lineHeight: 1.04,
    fontWeight: 700,
    letterSpacing: "-0.03em",
  };

  const subtitleStyle: React.CSSProperties = {
    color: "#64748B",
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 18,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    marginBottom: 8,
  };

  const fieldGroupStyle: React.CSSProperties = {
    width: "100%",
    marginBottom: 16,
  };

  const inputWrapStyleBase: React.CSSProperties = {
    width: "100%",
    minHeight: 58,
    borderRadius: 16,
    border: "1px solid #D7DEE6",
    background: "#F8FAFC",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    boxSizing: "border-box",
    position: "relative",
    transition: "all .18s ease",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 500,
    fontFamily: "Poppins, sans-serif",
    paddingRight: 42,
  };

  const eyeButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748B",
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    minHeight: 58,
    borderRadius: 16,
    border: "none",
    cursor: loading ? "not-allowed" : "pointer",
    background: "#0E323A",
    color: "#FFFFFF",
    fontFamily: "Poppins, sans-serif",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .18s ease",
    marginBottom: 18,
    opacity: loading ? 0.78 : 1,
  };

  const dividerWrapStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "10px 0 18px",
  };

  const dividerLineStyle: React.CSSProperties = {
    flex: 1,
    height: 1,
    background: "rgba(148, 163, 184, 0.28)",
  };

  const dividerTextStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: "#94A3B8",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  const socialGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: 12,
    width: "100%",
    alignItems: "stretch",
  };

  const bottomRowStyle: React.CSSProperties = {
    marginTop: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  };

  const mutedTextStyle: React.CSSProperties = {
    color: "#64748B",
    fontSize: 13,
    lineHeight: 1.6,
  };

  const feedbackStyle = (type: "error" | "success"): React.CSSProperties => ({
    width: "100%",
    marginBottom: 16,
    borderRadius: 14,
    padding: "13px 14px",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.5,
    border:
      type === "error"
        ? "1px solid rgba(220, 38, 38, 0.14)"
        : "1px solid rgba(22, 163, 74, 0.14)",
    background:
      type === "error" ? "rgba(254, 242, 242, 1)" : "rgba(240, 253, 244, 1)",
    color: type === "error" ? "#B91C1C" : "#15803D",
  });

  return (
    <div style={pageStyle}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: 42,
            left: -70,
            top: 24,
            transform: "rotate(28deg)",
            background: "rgba(255, 156, 36, 0.50)",
            border: "1px solid rgba(255, 156, 36, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: 34,
            left: "11%",
            top: "18%",
            transform: "rotate(45deg)",
            background: "rgba(26, 103, 191, 0.50)",
            border: "1px solid rgba(26, 103, 191, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 290,
            height: 290,
            borderRadius: 42,
            right: "8%",
            top: 56,
            transform: "rotate(18deg)",
            background: "rgba(0, 192, 183, 0.44)",
            border: "1px solid rgba(0, 192, 183, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 160,
            height: 160,
            borderRadius: 22,
            right: "19%",
            top: "31%",
            transform: "rotate(-22deg)",
            background: "rgba(0, 177, 231, 0.65)",
            border: "1px solid rgba(0, 177, 231, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            right: -120,
            bottom: -120,
            background: "rgba(26, 103, 191, 0.30)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 240,
            height: 240,
            borderRadius: "50%",
            left: -80,
            bottom: "10%",
            background: "rgba(0, 192, 183, 0.38)",
            filter: "blur(1px)",
          }}
        />
      </div>

      <div style={cardStyle}>
        <div style={formTitleStyle}>Redefinir senha</div>

        {feedback && (
          <div style={feedbackStyle(feedback.type)} role="alert">
            {feedback.msg}
          </div>
        )}

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>ID do usuário ou e-mail corporativo</label>
          <div
            style={inputWrapStyleBase}
            onFocusCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#0E323A";
              (e.currentTarget as HTMLDivElement).style.background = "#FFFFFF";
            }}
            onBlurCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#D7DEE6";
              (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
            }}
          >
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="seuemail@empresa.com ou ID"
              autoComplete="username"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Nova senha</label>
          <div
            style={inputWrapStyleBase}
            onFocusCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#0E323A";
              (e.currentTarget as HTMLDivElement).style.background = "#FFFFFF";
            }}
            onBlurCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#D7DEE6";
              (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
            }}
          >
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Digite a nova senha"
              autoComplete="new-password"
              style={inputStyle}
            />

            <button
              type="button"
              onClick={() => setShowNewPassword((v) => !v)}
              aria-label={showNewPassword ? "Ocultar senha" : "Mostrar senha"}
              style={eyeButtonStyle}
            >
              {showNewPassword ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Confirmar nova senha</label>
          <div
            style={inputWrapStyleBase}
            onFocusCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#0E323A";
              (e.currentTarget as HTMLDivElement).style.background = "#FFFFFF";
            }}
            onBlurCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#D7DEE6";
              (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
            }}
          >
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Confirme a nova senha"
              autoComplete="new-password"
              style={inputStyle}
            />

            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={
                showConfirmPassword ? "Ocultar senha" : "Mostrar senha"
              }
              style={eyeButtonStyle}
            >
              {showConfirmPassword ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleResetPassword}
          disabled={loading}
          style={primaryButtonStyle}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          {loading ? "Redefinindo..." : "Redefinir senha"}
        </button>

        <div style={dividerWrapStyle}>
          <div style={dividerLineStyle} />
        </div>

        <div style={bottomRowStyle}>
          <div style={mutedTextStyle}>
            Lembrou a senha?{" "}
            <Link
              to="/entrar"
              style={{
                color: "#0E323A",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Voltar para login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecuperarSenhaContainer;
